import { useEffect, useRef, useState } from 'react';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import { FeedbackMenu } from './FeedbackMenu.jsx';
import { countFeedbackMarks, countFeedbackScreens } from './feedbackSessionCounts.js';

export function FeedbackLauncher() {
  const { state, openFeedback, closeFeedback } = useFeedbackMarkup();
  const rootRef = useRef(null);
  const [coachDismissed, setCoachDismissed] = useState(() => {
    try {
      return window.localStorage.getItem('dha_nexus_feedback_coach_dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const saveFailed = state.saveStatus === 'failed';
  const screenCount = countFeedbackScreens(state);
  const markCount = countFeedbackMarks(state);
  const feedbackActive = screenCount > 0;
  // An inactive meaningful snapshot for the current actor remains a distinct
  // recovery affordance. Active and idle sessions use the ordinary label.
  const offersResume = Boolean(state.resumableDraft && !state.package);
  const launcherLabel = offersResume ? 'Resume feedback' : 'Feedback';
  const activeDescription = feedbackActive
    ? `Feedback active — ${screenCount} screen${screenCount === 1 ? '' : 's'}, ${markCount} mark${
        markCount === 1 ? '' : 's'
      }`
    : launcherLabel;
  const launcherDescription = saveFailed ? `${activeDescription} — save failed` : activeDescription;
  // Session-only auto-hide: after ~10s the tip stops covering content, but the
  // user never deliberately dismissed it, so it may return next session.
  const [coachAutoHidden, setCoachAutoHidden] = useState(false);
  const showCoachmark =
    !state.overlayOpen && screenCount === 0 && !coachDismissed && !coachAutoHidden && !offersResume;
  const launcherClasses = [
    'feedback-launcher',
    'fb-btn',
    state.overlayOpen ? 'active' : '',
    feedbackActive ? 'feedback-launcher-session-active' : '',
    saveFailed ? 'feedback-launcher-save-failed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (!state.overlayOpen) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closeFeedback();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [closeFeedback, state.overlayOpen]);

  function dismissCoach() {
    try {
      window.localStorage.setItem('dha_nexus_feedback_coach_dismissed', 'true');
    } catch {
      // The interaction still works when storage is unavailable.
    }
    setCoachDismissed(true);
  }

  // Clicking anywhere outside the launcher area is a deliberate "not now" —
  // dismiss the tip permanently rather than letting it re-cover page content.
  useEffect(() => {
    if (!showCoachmark) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) dismissCoach();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [showCoachmark]);

  useEffect(() => {
    if (!showCoachmark) return undefined;
    const timer = window.setTimeout(() => setCoachAutoHidden(true), 10_000);
    return () => window.clearTimeout(timer);
  }, [showCoachmark]);

  function handleLauncherClick() {
    dismissCoach();
    if (state.overlayOpen) closeFeedback();
    else openFeedback();
  }

  return (
    <div className="feedback-launcher-wrap" ref={rootRef}>
      <button
        type="button"
        className={launcherClasses}
        data-testid="feedback-launcher"
        aria-haspopup="menu"
        aria-expanded={state.overlayOpen}
        aria-pressed={state.overlayOpen}
        aria-label={launcherDescription}
        onClick={handleLauncherClick}
        title={launcherDescription}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 11v3a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1z" />
          <path d="M14 8c1.5 1 1.5 6 0 7" />
          <path d="M17.5 5c3 2.5 3 11 0 13.5" />
        </svg>
        <span className="feedback-launcher-label">{launcherLabel}</span>
        {feedbackActive ? (
          <span
            className="feedback-launcher-active-cue"
            data-testid="feedback-launcher-active"
            aria-hidden="true"
          >
            Active
          </span>
        ) : null}
        {feedbackActive ? (
          <span
            className="feedback-launcher-count fb-cnt"
            data-testid="feedback-launcher-count"
            aria-hidden="true"
          >
            {screenCount}
          </span>
        ) : null}
        {saveFailed ? (
          <span
            className="feedback-launcher-dot feedback-launcher-error-dot"
            role="img"
            aria-label="Feedback save failed"
          />
        ) : null}
      </button>
      {state.overlayOpen ? <FeedbackMenu /> : null}
      {showCoachmark ? (
        // The tip body is a generous mouse target for "make this go away";
        // the close button is the accessible affordance for the same action.
        <div
          className="feedback-coachmark coach"
          data-testid="feedback-coachmark"
          role="status"
          onClick={dismissCoach}
        >
          <button
            type="button"
            className="feedback-coachmark-close"
            data-testid="feedback-coachmark-close"
            aria-label="Dismiss this tip"
            onClick={dismissCoach}
          >
            ×
          </button>
          <b>Spot something you&apos;d change?</b> Click <b>Feedback</b> — you can send a marked-up
          screenshot in under a minute.
        </div>
      ) : null}
    </div>
  );
}
